import { type ReactNode, useState } from "react";

import { type ProfileOption, ProfileSelect } from "./ProfileSelect";
import { IconArrowUpDown, IconChart, IconSearch } from "./icons";

export type Page = "search" | "results";

interface SidebarProps {
  page: Page;
  onNav: (p: Page) => void;
  modeProfiles: ProfileOption[];
  profileA: string | null;
  profileB: string | null;
  onPickA: (api_name: string) => void;
  onPickB: (api_name: string) => void;
  onSwap: () => void;
  canSwap: boolean;
  buildLabel: string;
}

/**
 * 272-wide left rail. Nav at the top (Search + Results), divider, then the
 * two Profile slot pickers with a small swap button between them. Build
 * footer pinned at the bottom. The slot pickers are the primary arena
 * interaction surface — everything else on this rail exists to frame them.
 */
export function Sidebar({
  page,
  onNav,
  modeProfiles,
  profileA,
  profileB,
  onPickA,
  onPickB,
  onSwap,
  canSwap,
  buildLabel,
}: SidebarProps) {
  return (
    <aside
      style={{
        width: 272,
        flexShrink: 0,
        borderRight: "1px solid var(--border)",
        background: "var(--sidebar)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: 14, display: "grid", gap: 2 }}>
        <NavItem
          active={page === "search"}
          onClick={() => onNav("search")}
          label="Search"
          icon={<IconSearch size={15} />}
        />
        <NavItem
          active={page === "results"}
          onClick={() => onNav("results")}
          label="Results"
          icon={<IconChart size={15} />}
        />
      </div>
      <div
        style={{
          height: 1,
          background: "var(--sidebar-border)",
          margin: "4px 14px 10px",
        }}
      />
      <div
        style={{
          padding: "0 14px 4px",
          fontSize: 11,
          color: "var(--muted-foreground)",
          lineHeight: 1.45,
        }}
      >
        Pick two profiles to compare.
      </div>
      <div style={{ padding: "8px 14px 0", display: "grid", gap: 6 }}>
        <ProfileSelect
          slotLabel="A"
          slotColor="var(--atf-pink-600)"
          value={profileA}
          onChange={onPickA}
          options={modeProfiles}
          exclude={profileB}
        />
        <div style={{ display: "flex", justifyContent: "center", padding: "2px 0" }}>
          <SwapButton onClick={onSwap} disabled={!canSwap} />
        </div>
        <ProfileSelect
          slotLabel="B"
          slotColor="var(--atf-blue-950)"
          value={profileB}
          onChange={onPickB}
          options={modeProfiles}
          exclude={profileA}
        />
      </div>
      <div style={{ flex: 1 }} />
      <div
        style={{
          padding: 14,
          borderTop: "1px solid var(--sidebar-border)",
          fontSize: 11,
          color: "var(--muted-foreground)",
          lineHeight: 1.4,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            letterSpacing: "0.04em",
          }}
        >
          BUILD
        </div>
        <div
          style={{
            marginTop: 2,
            color: "var(--foreground)",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
          }}
        >
          {buildLabel}
        </div>
      </div>
    </aside>
  );
}

interface NavItemProps {
  active: boolean;
  onClick: () => void;
  label: string;
  icon: ReactNode;
}

function NavItem({ active, onClick, label, icon }: NavItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        padding: "8px 10px",
        borderRadius: 8,
        border: "none",
        cursor: "pointer",
        fontFamily: "var(--font-sans)",
        fontSize: 13,
        fontWeight: 500,
        textAlign: "left",
        background: active ? "var(--sidebar-accent)" : "transparent",
        color: active ? "var(--atf-pink-700)" : "var(--foreground)",
        transition: "background .15s",
      }}
    >
      {icon}
      {label}
    </button>
  );
}

function SwapButton({ onClick, disabled }: { onClick: () => void; disabled: boolean }) {
  const [hover, setHover] = useState(false);
  const [focused, setFocused] = useState(false);
  return (
    <button
      type="button"
      aria-label="Swap Profile A and Profile B"
      title="Swap Profile A and Profile B"
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        width: 28,
        height: 28,
        borderRadius: 8,
        background: "var(--background)",
        color: disabled ? "var(--muted-foreground)" : "var(--foreground)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        border: `1px solid ${hover && !disabled ? "var(--atf-pink-700)" : "var(--border)"}`,
        boxShadow: focused && !disabled ? "inset 0 0 0 2px var(--atf-pink-700)" : "none",
        transition: "border-color 120ms ease, box-shadow 120ms ease, opacity 120ms ease",
        outline: "none",
      }}
    >
      <IconArrowUpDown size={14} />
    </button>
  );
}
